/**
 * Sale Modal
 * Modal para adicionar uma venda de consórcio
 */

import { useState, useEffect, useRef } from 'react';
import { formatBRL as formatCurrency } from '@/lib/format';
import { logAuditEvent } from '../auditoria/logAudit';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useGetList, useNotify, useDataProvider, useGetIdentity, useCreatePath } from 'ra-core';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { ContactCreateDialog } from '../contacts/ContactCreate';
import { sellServopa, type SellError } from '@/lib/api-client';
import { stripNonDigits, validateFullName } from '../contacts/document-validation';
import { getContactDetail, getContactDisplayName } from '../contacts/contact-details';
import type { Contact, Sale } from '../types';
import { getParcelaOptions, rendaMinima, checkRenda } from './sale-options';
import { EstrategiaContemplacaoCampo } from '../commons/EstrategiaContemplacaoCampo';
import { lanceFixoAutomaticoPara, type EstrategiaContemplacao } from '../commons/estrategiaContemplacao';

interface Category {
  id: string;
  categoria_code: string;
  categoria_nome: string | null;
  valor_credito: string | number | null;
  prazo: number | null;
  percentual_mensal: string | number | null;
  parcela_sem_seguro: string | number | null;
  valor_seguro: string | number | null;
  total_parcela: string | number | null;
  observacao: string | null;
  purchase_url?: string | null;
}

interface GroupDetails {
  group_number: number;
  vencimento_dia: number | null;
  taxa_admin: string | null;
  fundo_reserva: string | null;
  lance_embutido: string | null;
  numero_consorciados: number | null;
  tipo_seguro: string | null;
}

interface SaleModalProps {
  open: boolean;
  onClose: () => void;
  groupNumber: number;
  category: Category | null;
  segment: 'M' | 'I';
  adminFee?: number | null;
  selectedCota?: string | null;
  isFlex?: boolean;
  initialContactId?: number | null;
}

type ConfirmationData = {
  confirmationId: string | null;
  contractUrl: string | null;
  boletoUrl: string | null;
  linkUrl: string | null;
  pixKey: string | null;
  qrCodeData: string | null;
  qrCodeUrl: string | null;
  paymentMethod: string | null;
};

const isUrl = (value: string) => /^https?:\/\//i.test(value);

const normalizeQrCode = (value: string | null | undefined) => {
  if (!value) return { qrCodeData: null as string | null, qrCodeUrl: null as string | null };
  if (value.startsWith('data:image')) return { qrCodeData: value, qrCodeUrl: null };
  if (isUrl(value)) return { qrCodeData: null, qrCodeUrl: value };
  return { qrCodeData: `data:image/png;base64,${value}`, qrCodeUrl: null };
};

const buildMockQrCode = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256">
  <rect width="256" height="256" fill="#ffffff"/>
  <rect x="24" y="24" width="64" height="64" fill="#000000"/>
  <rect x="168" y="24" width="64" height="64" fill="#000000"/>
  <rect x="24" y="168" width="64" height="64" fill="#000000"/>
  <rect x="120" y="120" width="24" height="24" fill="#000000"/>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const getLocalIsoDate = (value: Date = new Date()) => {
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60_000);
  return local.toISOString().split('T')[0];
};

// Extract contract number from PDF URL patterns like:
// ...BOL_32029I10_20260221_203356.pdf or ...CTR_32029I10_20260221_203356.pdf
const extractContractFromUrl = (url: string | null): string | null => {
  if (!url) return null;
  const match = url.match(/(?:BOL|CTR)_([A-Z0-9]+)_/i);
  return match?.[1] || null;
};

// Extrai os dados de confirmação (contrato, boleto, pix, link) do result do /sell.
// Usado no caminho de sucesso E no fallback: quando o transporte cai mas o backend
// registrou a venda, relemos esses campos do api_response gravado na linha.
function buildConfirmationFromResult(
  sellResponse: any,
  paymentMethodFallback?: string,
): ConfirmationData & { contractNumber: string | null } {
  const confirmation = sellResponse?.confirmation || {};
  const payment = confirmation?.payment || sellResponse?.payment || {};
  const confirmationId =
    confirmation?.id || sellResponse?.confirmation_id || sellResponse?.sale_id || null;

  const contractUrl =
    confirmation?.contractLink ||
    confirmation?.contract_url ||
    confirmation?.url_contrato ||
    confirmation?.url ||
    sellResponse?.contract_url ||
    sellResponse?.url_contrato ||
    null;

  let contractNumber = sellResponse?.contrato || sellResponse?.contract_number || null;
  if (!contractNumber && contractUrl) contractNumber = extractContractFromUrl(contractUrl);

  const boletoUrl =
    confirmation?.boletoLink ||
    payment?.boleto_url ||
    payment?.url_boleto ||
    sellResponse?.url_boleto ||
    null;
  if (!contractNumber && boletoUrl) contractNumber = extractContractFromUrl(boletoUrl);

  const linkUrl =
    confirmation?.paymentLink ||
    payment?.url ||
    payment?.link ||
    payment?.payment_url ||
    sellResponse?.url_link_pagamento ||
    null;
  const pixKey =
    confirmation?.qrCode ||
    payment?.pix_key ||
    payment?.chave_pix ||
    sellResponse?.chave_pix ||
    null;
  const qrCodeRaw =
    confirmation?.qrImage ||
    payment?.qr_code ||
    payment?.qrcode ||
    sellResponse?.qr_code ||
    null;
  const { qrCodeData, qrCodeUrl } = normalizeQrCode(typeof qrCodeRaw === 'string' ? qrCodeRaw : null);
  const paymentMethod = String(payment?.method || paymentMethodFallback || '').toLowerCase();

  return {
    confirmationId,
    contractUrl,
    boletoUrl,
    linkUrl,
    pixKey,
    qrCodeData,
    qrCodeUrl,
    paymentMethod,
    contractNumber,
  };
}

export const SaleModal = ({ open, onClose, groupNumber, category, segment, adminFee = null, selectedCota, isFlex = false, initialContactId = null }: SaleModalProps) => {
  const [groupDetails, setGroupDetails] = useState<GroupDetails | null>(null);
  const [saving, setSaving] = useState(false);
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const createPath = useCreatePath();
  const navigate = useNavigate();
  const contactSearchRef = useRef<HTMLInputElement | null>(null);
  const [selectedContactName, setSelectedContactName] = useState<string>('');
  const [selectedContactRecord, setSelectedContactRecord] = useState<Contact | null>(null);

  // Form state
  const [leadId, setLeadId] = useState<string>('');
  const [hasInsurance, setHasInsurance] = useState<boolean>(true);
  const [selectedParcela, setSelectedParcela] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('boleto');
  const [dueDate, setDueDate] = useState<string>('');
  const [showContactCreate, setShowContactCreate] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationData, setConfirmationData] = useState<ConfirmationData | null>(null);
  // Erro de venda mostrado em MODAL persistente (não toast) — mensagens como
  // "Refaça a venda COM seguro" não podem sumir antes do vendedor ler.
  const [saleError, setSaleError] = useState<string | null>(null);
  // A falha morreu no meio do caminho (dá pra insistir) ou foi recusa da Servopa
  // com motivo dela (insistir dá o mesmo não)? Decide o botão "Tentar de novo".
  const [salePodeTentarDeNovo, setSalePodeTentarDeNovo] = useState(false);
  // Conta de demonstração (migration 192) preenche tudo, mas não finaliza:
  // a venda nunca sai para a Servopa nem é gravada.
  const [avisoDemoOpen, setAvisoDemoOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState<string>('');
  const [debouncedContactSearch, setDebouncedContactSearch] = useState<string>('');
  const [debugMode, setDebugMode] = useState<boolean>(false);
  const [contactPreviewOpen, setContactPreviewOpen] = useState<boolean>(false);
  const [confirmSaleOpen, setConfirmSaleOpen] = useState<boolean>(false);
  // Estratégia de contemplação (migration 237): o que o cliente quer. Nasce
  // 'nenhuma' (não combinado) com o fixo automático ligado, como sempre foi;
  // sorteio e lance fixo definem o fixo automático sozinhos.
  const [estrategia, setEstrategia] = useState<EstrategiaContemplacao>('nenhuma');
  const [lanceFixoAuto, setLanceFixoAuto] = useState<boolean>(true);
  const isMockSale = Boolean(import.meta.env.DEV) && groupNumber === 9999 && category?.categoria_code === 'A1';

  useEffect(() => {
    if (!saving) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saving]);

  // Get the user record ID for the current user
  const { data: userRecords } = useGetList<Sale>('users', {
    filter: identity?.id ? { user_id: identity.id } : { id: -1 },
    pagination: { page: 1, perPage: 1 },
  });
  const currentUserId = userRecords?.[0]?.id;

  // Debounce contact search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedContactSearch(contactSearch), 300);
    return () => clearTimeout(t);
  }, [contactSearch]);

  // Load contacts only when user is searching
  const { data: contacts, refetch: refetchContacts } = useGetList('contacts', {
    filter: debouncedContactSearch.length >= 2 ? { q: debouncedContactSearch } : { id: -999 },
    pagination: { page: 1, perPage: 30 },
    sort: { field: 'details->>name', order: 'ASC' },
  });

  // Load group details when modal opens
  useEffect(() => {
    if (open && groupNumber) {
      loadGroupDetails();
      // Set default due date to next month
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      setDueDate(nextMonth.toISOString().split('T')[0]);
      // Reset contact search
      setContactSearch('');
    }
  }, [open, groupNumber]);

  useEffect(() => {
    if (!open || !initialContactId) return;

    let cancelled = false;

    const loadInitialContact = async () => {
      try {
        const result = await dataProvider.getOne<Contact>('contacts', { id: initialContactId });
        if (cancelled || !result?.data) return;

        const contact = result.data;
        setLeadId(String(contact.id));
        setSelectedContactName(getContactDisplayName(contact));
        setSelectedContactRecord(contact);
      } catch (error) {
        console.error('Error loading initial contact for sale:', error);
      }
    };

    loadInitialContact();

    return () => {
      cancelled = true;
    };
  }, [open, initialContactId, dataProvider]);

  // Update insurance when category or group details change (enforce mandatory insurance)
  useEffect(() => {
    if (category && groupDetails) {
      const obs = category?.observacao?.toUpperCase() || '';
      const tipoSeguro = groupDetails?.tipo_seguro?.toUpperCase() || '';
      
      // Force insurance on if mandatory
      if (obs.includes('OBRIGATÓRIO') || obs.includes('OBRIGATORIO') || 
          tipoSeguro.includes('OBRIGATÓRIO') || tipoSeguro.includes('OBRIGATORIO')) {
        setHasInsurance(true);
      }
    }
  }, [category, groupDetails]);

  const loadGroupDetails = async () => {
    try {
      // Mock data for group 9999 in dev
      if (import.meta.env.DEV && groupNumber === 9999) {
        setGroupDetails({
          group_number: 9999,
          lance_embutido: '0',
          numero_consorciados: 100,
          vencimento_dia: 15,
          fundo_reserva: '2.50',
          taxa_admin: '18.00',
          tipo_seguro: 'OPCIONAL',
        });
        return;
      }

      const { data, error } = await supabase
        .from('servopa_group_details')
        .select('*')
        .eq('group_number', groupNumber)
        .maybeSingle();

      if (error) throw error;

      setGroupDetails((data as any) || {
        group_number: groupNumber,
        lance_embutido: null,
        numero_consorciados: null,
        vencimento_dia: null,
        fundo_reserva: null,
        taxa_admin: null,
        tipo_seguro: null,
      });
    } catch (error) {
      console.error('Error loading group details:', error);
      notify('Não foi possível carregar os detalhes do grupo.', { type: 'error' });
    }
  };

  // Determine insurance requirement type from observacao
  const getInsuranceRequirement = (): 'mandatory' | 'at_contemplation' | 'optional' => {
    const obs = category?.observacao?.toUpperCase() || '';
    const tipoSeguro = groupDetails?.tipo_seguro?.toUpperCase() || '';
    
    // Check if insurance is at contemplation
    if (obs.includes('CONTEMPLAÇÃO') || obs.includes('CONTEMPLACAO')) {
      return 'at_contemplation';
    }
    
    // Check if insurance is mandatory
    if (obs.includes('OBRIGATÓRIO') || obs.includes('OBRIGATORIO') || 
        tipoSeguro.includes('OBRIGATÓRIO') || tipoSeguro.includes('OBRIGATORIO')) {
      return 'mandatory';
    }
    
    return 'optional';
  };

  const insuranceRequirement = getInsuranceRequirement();
  const insuranceLabel = {
    mandatory: 'Seguro obrigatório',
    at_contemplation: 'Seguro na contemplação',
    optional: 'Seguro opcional'
  }[insuranceRequirement];

  const parcelaOptions = getParcelaOptions(category, { hasInsurance, isFlex });

  // Pre-sale data quality checks. Both surface an inline warning and disable
  // the confirm button so the vendedor doesn't waste a Servopa round-trip
  // just to discover the data is bad.

  // Servopa rejeita venda com nome simples (e.g. "João"). Cadastro permite
  // pra não bloquear lead capture, mas no momento de vender exigimos sobrenome.
  const nameCheck = (() => {
    if (!selectedContactRecord) return null;
    const name = String(getContactDetail(selectedContactRecord, 'name', '') || '');
    const error = validateFullName(name);
    return { ok: !error, name, error };
  })();

  // Income check: Servopa exige renda mensal >= 3 × parcela. Em vez de só
  // disparar erro no submit, deixa a falta de renda visível no form e bloqueia
  // o botão "Confirmar e vender" do modal de confirmação.
  const rendaCheck = (() => {
    if (!selectedContactRecord || !selectedParcela) return null;
    const opt = parcelaOptions.find(p => p.value === selectedParcela);
    if (!opt) return null;
    const renda = Number(getContactDetail(selectedContactRecord, 'monthly_income') ?? 0);
    const minRenda = rendaMinima(opt.amount);
    const fmt = (v: number) => formatCurrency(v);
    return {
      ok: checkRenda(renda, opt.amount),
      renda,
      minRenda,
      rendaFmt: renda > 0 ? fmt(renda) : 'não informada',
      minRendaFmt: fmt(minRenda),
    };
  })();

  // Reset parcela selection when insurance changes
  useEffect(() => {
    setSelectedParcela('');
  }, [hasInsurance]);

  // Handle contact creation success
  const handleContactCreated = async (contact: Contact) => {
    // Refetch contacts to include the new one
    await refetchContacts();
    // Auto-select the newly created contact
    setLeadId(String(contact.id));
      setSelectedContactName(getContactDisplayName(contact));
      setSelectedContactRecord(contact);
      setContactSearch('');
      setDebouncedContactSearch('');
    setShowContactCreate(false);
    notify('Contato criado e selecionado.', { type: 'success' });
  };

  const resetForm = () => {
    setLeadId('');
      setSelectedContactName('');
      setSelectedContactRecord(null);
      setContactSearch('');
      setDebouncedContactSearch('');
    setSelectedParcela('');
    setPaymentMethod('boleto');
    setEstrategia('nenhuma');
    setLanceFixoAuto(true);
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
    setConfirmationData(null);
    onClose();
    resetForm();
  };

  const handleSave = async () => {
    // Validation
    if (!leadId) {
      notify('Selecione um lead', { type: 'warning' });
      return;
    }
    if (!selectedParcela) {
      notify('Selecione uma parcela', { type: 'warning' });
      return;
    }
    if (!dueDate) {
      notify('Selecione a data de vencimento', { type: 'warning' });
      return;
    }
    const usePurchaseUrl = Boolean(category?.purchase_url);
    if (!usePurchaseUrl && !isMockSale) {
      notify('URL de compra não informada para esta categoria.', { type: 'warning' });
      return;
    }
    if (identity?.is_demo) {
      setAvisoDemoOpen(true);
      return;
    }

    setSaving(true);
    let attemptId: string | null = null;
    // Snapshot do que o vendedor preencheu — hoisted p/ ficar acessível também no
    // catch (auditoria registra tentativas que falham, não só as que dão certo).
    let auditSellerInput: Record<string, unknown> | null = null;

    try {
      const selectedParcelaOption = parcelaOptions.find(p => p.value === selectedParcela);
      if (!selectedParcelaOption) throw new Error('Parcela não encontrada');

      const valorCredito = typeof category?.valor_credito === 'number' 
        ? category.valor_credito 
        : parseFloat(String(category?.valor_credito || 0));

      const selectedContact = selectedContactRecord || (contacts || []).find(
        (contact: Contact) => String(contact.id) === String(leadId)
      ) as Contact | undefined;

      if (!selectedContact) {
        notify('Contato não encontrado.', { type: 'error' });
        return;
      }

      const contactEmail = String(getContactDetail(selectedContact, 'email', '') || '');
      const contactPhone = String(getContactDetail(selectedContact, 'phone', '') || '');
      const contactName = getContactDisplayName(selectedContact);
      const birthDateIso = String(getContactDetail(selectedContact, 'birth_date', '') || '');

      const formatDateToBr = (isoDate: string) => {
        if (!isoDate) return '';
        const [datePart] = isoDate.split('T');
        const [year, month, day] = datePart.split('-');
        if (!year || !month || !day) return '';
        return `${day}/${month}/${year}`;
      };

      const customerPayload = {
        tipo: String(getContactDetail(selectedContact, 'person_type', 'PF') || 'PF'),
        cpf: stripNonDigits(String(getContactDetail(selectedContact, 'cpf', '') || '')),
        name: contactName,
        birth: formatDateToBr(birthDateIso),
        // O robô marca o sexo na ficha do Servopa; sem isto ficava o padrão
        // deles e cliente masculino virava feminino no contrato.
        gender: String(getContactDetail(selectedContact, 'gender', '') || ''),
        email: contactEmail,
        phone: stripNonDigits(contactPhone),
        renda_mensal: getContactDetail(selectedContact, 'monthly_income') != null
          ? Number(getContactDetail(selectedContact, 'monthly_income')).toFixed(2)
          : '',
        cep: stripNonDigits(String(getContactDetail(selectedContact, 'zipcode', '') || '')),
        address: String(getContactDetail(selectedContact, 'address', '') || ''),
        number: String(getContactDetail(selectedContact, 'address_number', '') || ''),
        complement: String(getContactDetail(selectedContact, 'address_complement', 'N/A') || 'N/A'),
        neighborhood: String(getContactDetail(selectedContact, 'neighborhood', '') || ''),
        city: String(getContactDetail(selectedContact, 'city', '') || ''),
        state: String(getContactDetail(selectedContact, 'state', '') || ''),
      };

      const birthDateLabel = customerPayload.tipo === 'PJ' ? 'Data de criação' : 'Data de nascimento';

      const requiredFields = [
        { label: 'CPF/CNPJ', value: customerPayload.cpf },
        { label: 'Nome', value: customerPayload.name },
        { label: birthDateLabel, value: customerPayload.birth },
        { label: 'Email', value: customerPayload.email },
        { label: 'Telefone', value: customerPayload.phone },
        { label: 'Renda mensal', value: customerPayload.renda_mensal },
        { label: 'CEP', value: customerPayload.cep },
        { label: 'Endereco', value: customerPayload.address },
        { label: 'Numero', value: customerPayload.number },
        { label: 'Bairro', value: customerPayload.neighborhood },
        { label: 'Cidade', value: customerPayload.city },
        { label: 'Estado', value: customerPayload.state },
      ];

      const missingField = requiredFields.find((field) => !String(field.value || '').trim());
      if (missingField) {
        notify(`Preencha o campo obrigatório: ${missingField.label}`, { type: 'warning' });
        return;
      }

      // Validar renda mínima (deve ser >= 3x o valor da parcela)
      const rendaMensal = parseFloat(customerPayload.renda_mensal || '0');
      const valorParcela = selectedParcelaOption.amount;
      const rendaMinimaRequerida = rendaMinima(valorParcela);

      if (rendaMensal < rendaMinimaRequerida) {
        notify(
          `Renda mensal insuficiente: mínimo de 3x o valor da parcela (${formatCurrency(rendaMinimaRequerida)}).`,
          { type: 'error' }
        );
        return;
      }

      const paymentMethodMap: Record<string, string> = {
        boleto: 'boleto',
        pix: 'pix',
        cartao: 'link',
      };

      const paymentPayload = {
        method: paymentMethodMap[paymentMethod] || paymentMethod,
        boletoDate: paymentMethod === 'boleto' ? formatDateToBr(dueDate) : undefined,
      };

      const paymentMethodLabel: Record<string, string> = {
        boleto: 'BOLETO',
        pix: 'PIX',
        cartao: 'LINK DE PAGAMENTO',
      };

      // ======================================================================
      // 1. CRIAR REGISTRO DE TENTATIVA ANTES DE CHAMAR A API
      // ======================================================================
      if (!currentUserId) {
        notify('Usuário não identificado. Não foi possível criar a venda.', { type: 'error' });
        return;
      }

      const attemptData = {
        contact_id: parseInt(leadId),
        sales_person_id: currentUserId,
        status: 'started',
        venda_data: getLocalIsoDate(),
        cota: selectedCota || null,
        contrato: null, // Será preenchido pela API após sucesso
        group_number: groupNumber || null,
        has_insurance: hasInsurance,
        nome_consorciado: contactName,
        valor_parcela: selectedParcelaOption.amount,
        valor_credito: valorCredito,
        vencimento: dueDate,
        payment_method: paymentMethodLabel[paymentMethod] || paymentMethod.toUpperCase(),
        origem: 'AREA DO PARCEIRO',
        // pct_flex segue convenção Servopa: % que o cliente paga (100 = cheia, 50 = meia parcela).
        // No frontend, "Flex 50%" = desconto de 50% = cliente paga 50% → pct_flex = 100 - 50.
        is_flex: selectedParcelaOption.type === 'flex',
        pct_flex: selectedParcelaOption.type === 'flex'
          ? 100 - (selectedParcelaOption.flexPercentage ?? 0)
          : 100,
        estrategia_contemplacao: estrategia,
        lance_fixo_automatico: lanceFixoAutomaticoPara(estrategia, lanceFixoAuto),
        payload_sent: {
          options: {
            purchaseUrl: category?.purchase_url,
            // Tipo e % da parcela também: sem eles o venda-debug.sh refazia a
            // tentativa como parcela normal e parava no valor (03/11/2026).
            parcelaType: selectedParcelaOption.type,
            flexPercentage: selectedParcelaOption.flexPercentage,
            parcela: selectedParcelaOption.amount,
            seguro: hasInsurance,
            cota: selectedCota,
            customer: customerPayload,
            payment: paymentPayload,
          },
        },
      };

      // Captura tudo que o vendedor preencheu pra tentar a venda (cliente, cota,
      // parcela, pagamento, seguro). Vai pro log de auditoria nas duas saídas.
      auditSellerInput = {
        ...attemptData.payload_sent,
        valor_credito: attemptData.valor_credito,
        valor_parcela: attemptData.valor_parcela,
        cota: attemptData.cota,
        group_number: attemptData.group_number,
        has_insurance: attemptData.has_insurance,
        payment_method: attemptData.payment_method,
        is_flex: attemptData.is_flex,
        pct_flex: attemptData.pct_flex,
      };

      console.log('Creating servopa_vendas (pending):', attemptData);
      const attemptResult = await dataProvider.create('servopa_vendas', { data: attemptData });
      attemptId = attemptResult.data.id;

      if (isMockSale) {
        const mockId = `MOCK-${Date.now()}`;
        const contractUrl = `https://example.com/mock/contrato-${mockId}.pdf`;
        const boletoUrl = `https://example.com/mock/boleto-${mockId}.pdf`;
        const pixKey = `00020126460014BR.GOV.BCB.PIX0114mock@north.test52040000530398654041.235802BR5924NORTH MOCK PAGAMENTO6009SAO PAULO62070503***6304ABCD`;
        const qrCodeData = buildMockQrCode();

        await dataProvider.update('servopa_vendas', {
          id: attemptId,
          data: {
            status: 'pending',
            contrato: mockId,
            api_response: {
              raw: {
                success: true,
                mock: true,
                contract_url: contractUrl,
                boleto_url: paymentMethod === 'boleto' ? boletoUrl : null,
                pix_key: paymentMethod === 'pix' ? pixKey : null,
                qr_code: paymentMethod === 'pix' ? qrCodeData : null,
              },
              normalized: {
                confirmationId: mockId,
                contractUrl,
                boletoUrl: paymentMethod === 'boleto' ? boletoUrl : null,
                linkUrl: null,
                pixKey: paymentMethod === 'pix' ? pixKey : null,
                qrCodeData: paymentMethod === 'pix' ? qrCodeData : null,
                qrCodeUrl: null,
                paymentMethod: paymentMethod,
              },
            },
          },
          previousData: attemptData,
        });

        setConfirmationData({
          confirmationId: mockId,
          contractUrl,
          boletoUrl: paymentMethod === 'boleto' ? boletoUrl : null,
          linkUrl: null,
          pixKey: paymentMethod === 'pix' ? pixKey : null,
          qrCodeData: paymentMethod === 'pix' ? qrCodeData : null,
          qrCodeUrl: null,
          paymentMethod: paymentMethod,
        });
        setConfirmationOpen(true);
        notify('Venda mock registrada. Pagamento pendente de efetivação.', { type: 'info' });
        return;
      }

      // ======================================================================
      // 2. CHAMAR API COM O ATTEMPT_ID
      // ======================================================================
      // O backend tira o vendedor do token da sessão (não do corpo).
      const { data: sessao } = await supabase.auth.getSession();
      const accessToken = sessao.session?.access_token;
      if (!accessToken) {
        throw new Error('Sessão expirada. Entre novamente no sistema para vender.');
      }

      const sellResponse = await sellServopa({
        options: {
          purchaseUrl: category?.purchase_url || undefined,
          // We send the type+pct so the API matches the row directly. parcela
          // is kept only as a fallback / display value — Servopa rounds its
          // own flex amounts in ways our calc can drift from by a cent or two.
          parcelaType: selectedParcelaOption.type,
          flexPercentage: selectedParcelaOption.flexPercentage,
          parcela: selectedParcelaOption.amount,
          seguro: hasInsurance,
          cota: selectedCota || undefined,
          customer: customerPayload,
          payment: paymentPayload,
          debug: debugMode || undefined,
        },
        attempt_id: attemptId || undefined, // ID da tentativa para tracking
        access_token: accessToken,
        acting_user_id: identity?.id,
        acting_user_email: identity?.email,
      });

      // ======================================================================
      // 3. ATUALIZAR REGISTRO COMO SUCESSO
      // ======================================================================
      const {
        confirmationId,
        contractUrl,
        boletoUrl,
        linkUrl,
        pixKey,
        qrCodeData,
        qrCodeUrl,
        paymentMethod: paymentMethodResolved,
        contractNumber,
      } = buildConfirmationFromResult(sellResponse, paymentPayload.method);

      await dataProvider.update('servopa_vendas', {
        id: attemptId,
        data: {
          status: 'pending',
          contrato: contractNumber,
          api_response: {
            raw: sellResponse,
            normalized: {
              confirmationId,
              contractUrl,
              boletoUrl,
              linkUrl,
              pixKey,
              qrCodeData,
              qrCodeUrl,
              paymentMethod: paymentMethodResolved,
            },
          },
        },
        previousData: attemptData,
      });

      // A API deve ter salvado a venda no banco de dados Supabase
      
      setConfirmationData({
        confirmationId,
        contractUrl,
        boletoUrl,
        linkUrl,
        pixKey,
        qrCodeData,
        qrCodeUrl,
        paymentMethod: paymentMethodResolved,
      });
      setConfirmationOpen(true);
      notify('Venda registrada. Pagamento pendente de efetivação.', { type: 'info' });

      // Auditoria: tentativa de venda bem-sucedida (payload + resultado).
      void logAuditEvent({
        entity: 'venda',
        action: 'attempt',
        entityId: attemptId,
        summary: `${contactName} • Grupo ${groupNumber ?? '-'} • ${formatCurrency(valorCredito)}`,
        after: auditSellerInput,
        metadata: {
          outcome: 'success',
          contrato: contractNumber,
          confirmation_id: confirmationId,
          cota: selectedCota,
          group_number: groupNumber,
        },
      });
    } catch (error) {
      console.error('Error creating sale:', error);
      
      // Extrair mensagem de erro da API
      let errorMessage = 'Não foi possível criar a venda.';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        const errorObj = error as any;
        errorMessage = errorObj.message || errorObj.error || String(error);
      }
      // `diagnostic` (quando o backend manda) é o motivo técnico real da falha —
      // vai só pra auditoria/reconciliação. O vendedor vê apenas `errorMessage`
      // (genérica). Sem diagnostic, cai na própria errorMessage.
      const diagnostic = (error as any)?.diagnostic || errorMessage;
      
      // Fallback "à prova de bala": o transporte pode cair (524/timeout/aba
      // congelada) DEPOIS de a venda ter registrado server-side. Antes de tratar
      // como erro, conferimos a linha — se o backend já a marcou 'pending'/
      // 'confirmed', a venda É real: mostramos a confirmação, não o erro.
      if (attemptId) {
        try {
          const current = await dataProvider.getOne('servopa_vendas', { id: attemptId });
          const st = current?.data?.status;

          if (st === 'pending' || st === 'confirmed') {
            // O backend grava api_response = result cru; o front, quando chega a
            // gravar, usa { raw, normalized }. Cobrimos os dois.
            const stored = (current.data as any).api_response;
            const result = stored?.raw ?? stored ?? {};
            const recovered = buildConfirmationFromResult(result, paymentMethod);
            setConfirmationData({
              confirmationId: recovered.confirmationId,
              contractUrl: recovered.contractUrl,
              boletoUrl: recovered.boletoUrl,
              linkUrl: recovered.linkUrl,
              pixKey: recovered.pixKey,
              qrCodeData: recovered.qrCodeData,
              qrCodeUrl: recovered.qrCodeUrl,
              paymentMethod: recovered.paymentMethod,
            });
            setConfirmationOpen(true);
            notify('Venda registrada. Pagamento pendente de efetivação.', { type: 'info' });
            void logAuditEvent({
              entity: 'venda',
              action: 'attempt',
              entityId: attemptId,
              summary: `${selectedContactName} • Grupo ${groupNumber ?? '-'} • ${formatCurrency(valorCredito)}`,
              after: auditSellerInput,
              metadata: {
                outcome: 'success_recovered',
                contrato: recovered.contractNumber,
                confirmation_id: recovered.confirmationId,
                cota: selectedCota,
                group_number: groupNumber,
              },
            });
            return; // venda real recuperada — não trata como erro
          }

          // Só cancela se AINDA estiver 'started' (tentativa não-finalizada).
          // 'failed' violaria o check constraint; usamos 'cancelled' +
          // reconciliation_notes (a view deriva cancel_kind='nao_confirmada').
          // O backend já cancela o órfão server-side; isto é só fallback caso a
          // requisição nem tenha chegado lá.
          if (st === 'started') {
            await dataProvider.update('servopa_vendas', {
              id: attemptId,
              data: {
                status: 'cancelled',
                reconciliation_notes: `Falha na finalização: ${diagnostic}`,
                api_response: { error: diagnostic },
              },
              previousData: {},
            });
          }
        } catch (reconcileError) {
          console.error('Failed to reconcile sale attempt:', reconcileError);
        }
      }

      // Auditoria: tentativa de venda rejeitada/falha (ex.: renda 3x, seguro).
      void logAuditEvent({
        entity: 'venda',
        action: 'attempt_failed',
        entityId: attemptId,
        summary: diagnostic,
        after: auditSellerInput,
        metadata: {
          outcome: 'failed',
          error: diagnostic,
          user_message: errorMessage,
          group_number: groupNumber,
          cota: selectedCota,
        },
      });

      // Erro em MODAL persistente (não toast): mensagem fica até o vendedor fechar.
      // Sempre a genérica — o detalhe técnico ficou na auditoria/telegram.
      //
      // O formulário continua preenchido atrás deste modal (nada de resetForm
      // aqui), então "Tentar de novo" repete a venda com os mesmos dados num
      // clique — sem refazer o formulário nem passar pela confirmação de novo.
      //
      // Só aparece quando refazer é SEGURO (`podeRefazer`), e isso exige que o
      // backend tenha respondido: aí sabemos que nada ficou em voo. Quando o
      // transporte cai (524/timeout), a venda pode estar sendo processada agora
      // — repetir criaria contrato duplicado, então ali vale a orientação de
      // conferir na aba Vendas, não um botão de insistir.
      setSalePodeTentarDeNovo(Boolean((error as SellError)?.podeRefazer));
      setSaleError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!category) return null;

  const valorCredito = typeof category.valor_credito === 'number' 
    ? category.valor_credito 
    : parseFloat(String(category.valor_credito || 0));

  const segmentLabel = segment === 'M' ? 'Automóvel' : 'Imóvel';
  const requiredContactFields = [
    { key: 'person_type', label: 'Tipo' },
    { key: 'cpf', label: 'CPF/CNPJ' },
    { key: 'name', label: 'Nome' },
    { key: 'birth_date', label: 'Data de nascimento' },
  ];

  const getMissingContactFields = (contact: Contact) => {
    return requiredContactFields.filter(({ key }) => {
      const value = getContactDetail(contact, key);
      return !value;
    });
  };

  const handleSelectContact = (value: string) => {
    if (value === '__create__') {
      setShowContactCreate(true);
      return;
    }

    const selected = contacts?.find((contact: any) => String(contact.id) === value);
    if (!selected) {
      setLeadId(value);
      return;
    }

    const missingFields = getMissingContactFields(selected);
    if (missingFields.length === 0) {
      setLeadId(value);
      setSelectedContactName(getContactDisplayName(selected));
      setSelectedContactRecord(selected);
      setContactSearch('');
      setDebouncedContactSearch('');
      return;
    }

    const missingLabels = missingFields.map((field) => field.label).join(', ');
    const shouldEdit = window.confirm(
      `Este contato não possui informações obrigatórias (${missingLabels}). Deseja editar agora?`
    );

    if (shouldEdit) {
      const editPath = createPath({ resource: 'contacts', type: 'edit', id: selected.id });
      onClose();
      navigate(editPath);
      return;
    }

    notify('Preencha as informações obrigatórias para continuar.', { type: 'warning' });
    setLeadId('');
    setSelectedContactRecord(null);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !saving && onClose()}>
        <DialogContent
          className={`max-w-[calc(100%-1.5rem)] sm:max-w-[800px] max-h-[90vh] overflow-hidden transition ${
            showContactCreate ? 'pointer-events-none opacity-60 blur-sm' : ''
          }`}
        >
          {/* Fora da área que rola, cobrindo o modal inteiro: com o aviso
              dentro dela, rolar o modal para baixo levava o aviso junto para
              cima e a pessoa não via que a venda estava processando. */}
          {saving && (
            <div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-background/85 px-6 text-center">
              <div className="flex flex-col items-center gap-3">
                <Spinner size="large" />
                <div className="text-sm font-semibold">Processando venda...</div>
                <div className="text-xs text-muted-foreground">
                  Não feche nem recarregue esta página. Isso pode interromper a transação.
                </div>
              </div>
            </div>
          )}
          {/* A rolagem mora aqui dentro e trava enquanto a venda processa. */}
          <div className={`-mx-6 -my-6 max-h-[90vh] px-6 py-6 ${saving ? 'overflow-hidden' : 'overflow-y-auto'}`}>
            <DialogHeader>
              <DialogTitle>Adicionar venda</DialogTitle>
              <DialogDescription>
                {category.categoria_code} - {category.categoria_nome}
              </DialogDescription>
            </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Group Information */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
            <div>
              <div className="text-sm text-muted-foreground">Grupo</div>
              <div className="font-semibold">{groupNumber}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Segmento</div>
              <div className="font-semibold">{segmentLabel}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Valor do bem</div>
              <div className="font-semibold">{formatCurrency(valorCredito)}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Prazo</div>
              <div className="font-semibold">{category.prazo} meses</div>
            </div>
            {groupDetails?.vencimento_dia && (
              <div>
                <div className="text-sm text-muted-foreground">Vencimento</div>
                <div className="font-semibold">Dia {groupDetails.vencimento_dia}</div>
              </div>
            )}
            {groupDetails?.numero_consorciados && (
              <div>
                <div className="text-sm text-muted-foreground">Máx. participantes</div>
                <div className="font-semibold">{groupDetails.numero_consorciados}</div>
              </div>
            )}
            {groupDetails?.fundo_reserva && (
              <div>
                <div className="text-sm text-muted-foreground">Fundo de reserva</div>
                <div className="font-semibold">{parseFloat(String(groupDetails.fundo_reserva)).toFixed(2)}%</div>
              </div>
            )}
            {(adminFee != null || groupDetails?.taxa_admin) && (
              <div>
                <div className="text-sm text-muted-foreground">Taxa de administração</div>
                <div className="font-semibold">
                  {adminFee != null
                    ? `${Number(adminFee).toFixed(2)}%`
                    : `${parseFloat(String(groupDetails!.taxa_admin)).toFixed(2)}%`}
                </div>
              </div>
            )}
          </div>

          {/* Selected Cota */}
          {selectedCota && (
            <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Cota selecionada:
                </div>
                <div className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                  {selectedCota}
                </div>
              </div>
            </div>
          )}

          {/* Lead Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="contact-search" className="text-red-600">Contato *</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => handleSelectContact('__create__')}
              >
                + Criar contato
              </Button>
            </div>
            {leadId && (
              <>
                <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm bg-muted/40">
                  <span>{selectedContactName}</span>
                  <div className="flex items-center gap-3 ml-4">
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-800 text-xs underline"
                      onClick={() => setContactPreviewOpen(true)}
                    >
                      Ver
                    </button>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground text-xs"
                      onClick={() => { setLeadId(''); setSelectedContactName(''); setSelectedContactRecord(null); setContactSearch(''); setDebouncedContactSearch(''); }}
                    >
                      Trocar
                    </button>
                  </div>
                </div>
                {nameCheck && !nameCheck.ok && (
                  <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 dark:bg-red-950 dark:border-red-900 dark:text-red-200">
                    {nameCheck.error}. A Servopa rejeita vendas sem sobrenome;
                    edite o contato antes de prosseguir.
                  </div>
                )}
              </>
            )}
            {!leadId && (
              <div className="space-y-1">
                <Input
                  id="contact-search"
                  ref={contactSearchRef}
                  placeholder="Digite o nome do contato..."
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  className={contactSearch === '' ? 'border-red-500' : ''}
                  autoComplete="off"
                />
                {debouncedContactSearch.length >= 2 && (
                  <div className="rounded-md border bg-background shadow-sm max-h-48 overflow-y-auto">
                    {contacts && contacts.length > 0 ? (
                      contacts.map((contact: any) => (
                        <button
                          key={contact.id}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                          onClick={() => handleSelectContact(String(contact.id))}
                        >
                          {getContactDisplayName(contact)}
                        </button>
                      ))
                    ) : (
                      <p className="px-3 py-2 text-sm text-muted-foreground">Nenhum contato encontrado.</p>
                    )}
                  </div>
                )}
                {debouncedContactSearch.length < 2 && contactSearch.length > 0 && (
                  <p className="text-xs text-muted-foreground">Mínimo de 2 caracteres para a busca.</p>
                )}
                {contactSearch === '' && (
                  <p className="text-sm text-red-600">Selecione um contato</p>
                )}
              </div>
            )}
          </div>

          {/* Insurance */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="seguro">Seguro</Label>
              {/* "Seguro na Contemplação" só faz sentido quando o usuário NÃO
                  está cobrando o seguro agora — caso contrário "Com seguro"
                  já cobre a contemplação e o badge vira ruído. Mandatory e
                  optional seguem visíveis sempre. */}
              {(insuranceRequirement === 'mandatory' ||
                insuranceRequirement === 'optional' ||
                (insuranceRequirement === 'at_contemplation' && !hasInsurance)) && (
                <span className={`text-xs px-2 py-1 rounded ${
                  insuranceRequirement === 'mandatory' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                  insuranceRequirement === 'at_contemplation' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                }`}>
                  {insuranceLabel}
                </span>
              )}
            </div>
            <Select
              value={hasInsurance ? 'sim' : 'nao'}
              onValueChange={(v) => setHasInsurance(v === 'sim')}
              disabled={insuranceRequirement === 'mandatory'}
            >
              <SelectTrigger id="seguro">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sim">Com seguro</SelectItem>
                <SelectItem value="nao">Sem seguro</SelectItem>
              </SelectContent>
            </Select>
            {insuranceRequirement === 'mandatory' && (
              <p className="text-xs text-muted-foreground">
                Seguro obrigatório para este grupo.
              </p>
            )}
            {insuranceRequirement === 'at_contemplation' && !hasInsurance && (
              <p className="text-xs text-muted-foreground">
                ℹ️ Seguro será cobrado apenas após contemplação
              </p>
            )}
          </div>

          {/* Parcela Selection */}
          <div className="space-y-2">
            <Label htmlFor="parcela">Parcela</Label>
            <Select value={selectedParcela} onValueChange={setSelectedParcela}>
              <SelectTrigger id="parcela">
                <SelectValue placeholder="Selecione uma parcela" />
              </SelectTrigger>
              <SelectContent>
                {parcelaOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {rendaCheck && !rendaCheck.ok && (
              <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 dark:bg-red-950 dark:border-red-900 dark:text-red-200">
                Renda mensal do contato ({rendaCheck.rendaFmt}) é menor que o mínimo
                exigido pela Servopa: 3× a parcela = <strong>{rendaCheck.minRendaFmt}</strong>.
                A venda será rejeitada.
              </div>
            )}
          </div>

          {/* Estratégia de contemplação (+ fixo automático onde ele decide) */}
          <EstrategiaContemplacaoCampo
            idPrefixo="venda-nova"
            estrategia={estrategia}
            lanceFixoAutomatico={lanceFixoAuto}
            onChange={(e, auto) => {
              setEstrategia(e);
              setLanceFixoAuto(auto);
            }}
          />
          <p className="-mt-2 text-xs text-muted-foreground">Pode ser alterada depois nos detalhes da venda.</p>

          {/* Payment Information */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-semibold">Informações do pagamento</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="payment">Primeiro pagamento</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger id="payment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="boleto">Boleto bancário</SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="cartao">Link de pagamento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {paymentMethod === "boleto" && (
                <div className="space-y-2">
                  <Label htmlFor="dueDate">Data de vencimento</Label>
                  <Input
                    id="dueDate"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </div>
              )}
            </div>

          </div>
        </div>

          {/* Debug screenshot toggle (dev or ?debug=1) — for diagnosing things
              like "venda apareceu como sucesso mas o email nunca chegou". */}
          {(import.meta.env.DEV || new URLSearchParams(window.location.search).get('debug') === '1') && (
            <div className="px-1 pb-2 text-xs flex items-center gap-2">
              <input
                id="sale-debug"
                type="checkbox"
                checked={debugMode}
                onChange={(e) => setDebugMode(e.target.checked)}
              />
              <label htmlFor="sale-debug" className="cursor-pointer text-muted-foreground">
                Modo de depuração (registra capturas de tela e HTML de cada etapa)
              </label>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={() => setConfirmSaleOpen(true)} disabled={saving || !leadId || !selectedParcela}>
              {saving ? 'Salvando...' : 'Próximo'}
            </Button>
          </DialogFooter>
          </div>
        </DialogContent>

      </Dialog>

      {/* Contact Preview Modal — read-only quick view of the selected contact's data */}
      <Dialog open={contactPreviewOpen} onOpenChange={(isOpen) => !isOpen && setContactPreviewOpen(false)}>
        <DialogContent className="max-w-[calc(100%-1.5rem)] sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Dados do contato</DialogTitle>
            <DialogDescription>
              Confira as informações antes de prosseguir. Para editar, abra o contato no CRM.
            </DialogDescription>
          </DialogHeader>
          {selectedContactRecord && (() => {
            const c = selectedContactRecord;
            const cpfRaw = String(getContactDetail(c, 'cpf', '') || '');
            const cpfDigits = stripNonDigits(cpfRaw);
            const cpfFormatted = cpfDigits.length > 11
              ? cpfDigits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
              : cpfDigits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
            const fields: Array<[string, string]> = [
              ['Nome', getContactDisplayName(c) || '-'],
              ['Tipo', String(getContactDetail(c, 'person_type', 'PF') || 'PF')],
              ['CPF/CNPJ', cpfFormatted || '-'],
              ['Data nasc.', String(getContactDetail(c, 'birth_date', '') || '-')],
              ['Email', String(getContactDetail(c, 'email', '') || '-')],
              ['Telefone', String(getContactDetail(c, 'phone', '') || '-')],
              ['Renda mensal', getContactDetail(c, 'monthly_income') != null
                ? formatCurrency(Number(getContactDetail(c, 'monthly_income')))
                : '-'],
              ['CEP', String(getContactDetail(c, 'zipcode', '') || '-')],
              ['Endereço', `${String(getContactDetail(c, 'address', '') || '')}${getContactDetail(c, 'address_number') ? ', ' + getContactDetail(c, 'address_number') : ''}`.trim() || '-'],
              ['Bairro', String(getContactDetail(c, 'neighborhood', '') || '-')],
              ['Cidade/UF', `${String(getContactDetail(c, 'city', '') || '')}${getContactDetail(c, 'state') ? '/' + getContactDetail(c, 'state') : ''}`.trim() || '-'],
            ];
            return (
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm py-2">
                {fields.map(([label, value]) => (
                  <div key={label}>
                    <div className="text-xs text-muted-foreground">{label}</div>
                    <div className="font-medium break-words">{value}</div>
                  </div>
                ))}
              </div>
            );
          })()}
          <DialogFooter>
            <Button onClick={() => setContactPreviewOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-sale confirmation — venda é final, sem rollback do lado da Servopa */}
      <Dialog open={confirmSaleOpen} onOpenChange={(isOpen) => !isOpen && setConfirmSaleOpen(false)}>
        <DialogContent className="max-w-[calc(100%-1.5rem)] sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Confirmar venda</DialogTitle>
            <DialogDescription>
              Esta venda é <strong>definitiva</strong>. Após o envio, o contrato é gerado na Servopa
              e não pode ser desfeito pelo CRM. Confira os dados do contato antes de prosseguir.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm space-y-1">
            <div><span className="text-muted-foreground">Contato:</span> <span className="font-medium">{selectedContactName || '-'}</span></div>
            <div><span className="text-muted-foreground">Grupo / Cota:</span> <span className="font-medium">{groupNumber}{selectedCota ? ` / ${selectedCota}` : ''}</span></div>
            {(() => {
              const opt = parcelaOptions.find(p => p.value === selectedParcela);
              return opt ? (
                <div><span className="text-muted-foreground">Parcela:</span> <span className="font-medium">{opt.label}</span></div>
              ) : null;
            })()}
            {rendaCheck && (
              <div>
                <span className="text-muted-foreground">Renda mensal:</span>{' '}
                <span className={`font-medium ${rendaCheck.ok ? '' : 'text-red-700 dark:text-red-300'}`}>
                  {rendaCheck.rendaFmt}
                </span>{' '}
                <span className="text-xs text-muted-foreground">(mínimo: {rendaCheck.minRendaFmt})</span>
              </div>
            )}
          </div>
          {nameCheck && !nameCheck.ok && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950 dark:border-red-900 dark:text-red-200">
              Nome incompleto: <strong>{nameCheck.name || '(vazio)'}</strong>.
              A Servopa exige nome e sobrenome. Edite o contato antes de prosseguir.
            </div>
          )}
          {rendaCheck && !rendaCheck.ok && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950 dark:border-red-900 dark:text-red-200">
              Renda insuficiente. A Servopa exige <strong>3× o valor da parcela</strong>.
              Atualize a renda do contato ou selecione uma parcela menor; caso contrário,
              a venda será rejeitada.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSaleOpen(false)} disabled={saving}>
              Voltar
            </Button>
            <Button
              variant="outline"
              onClick={() => { setConfirmSaleOpen(true); setContactPreviewOpen(true); }}
              disabled={saving}
            >
              Conferir contato
            </Button>
            <Button
              onClick={() => { setConfirmSaleOpen(false); handleSave(); }}
              disabled={
                saving
                || (rendaCheck != null && !rendaCheck.ok)
                || (nameCheck != null && !nameCheck.ok)
              }
              className="bg-[#354660] text-white hover:bg-[#29374B]"
            >
              {saving ? 'Vendendo...' : 'Confirmar e vender'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Quick Create Modal */}
      <ContactCreateDialog
        open={showContactCreate}
        onClose={() => setShowContactCreate(false)}
        onSuccess={handleContactCreated}
        showLeadDialog={false}
      />

      <Dialog open={confirmationOpen} onOpenChange={(isOpen) => !isOpen && handleCloseConfirmation()}>
        <DialogContent className="max-w-[calc(100%-1.5rem)] sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Pagamento pendente</DialogTitle>
            <DialogDescription>
              Confira as informações de pagamento e contrato.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {confirmationData?.paymentMethod === 'pix' && (
              <div className="space-y-3">
                {confirmationData?.qrCodeData && (
                  <img
                    src={confirmationData.qrCodeData}
                    alt="QR Code Pix"
                    className="w-64 h-64 object-contain mx-auto"
                  />
                )}
                {confirmationData?.qrCodeUrl && (
                  <Button asChild variant="outline" className="w-full">
                    <a href={confirmationData.qrCodeUrl} target="_blank" rel="noreferrer">
                      Abrir QR Code Pix
                    </a>
                  </Button>
                )}
                {confirmationData?.pixKey && (
                  <Button
                    className="w-full"
                    onClick={() => navigator.clipboard.writeText(confirmationData.pixKey || '')}
                  >
                    Copiar chave Pix
                  </Button>
                )}
              </div>
            )}

            {confirmationData?.paymentMethod === 'boleto' && confirmationData?.boletoUrl && (
              <Button asChild className="w-full">
                <a href={confirmationData.boletoUrl} target="_blank" rel="noreferrer">
                  Abrir boleto
                </a>
              </Button>
            )}

            {(confirmationData?.paymentMethod === 'link' || confirmationData?.paymentMethod === 'cartao') && (
              confirmationData?.linkUrl ? (
                <div className="space-y-2">
                  <Button asChild className="w-full">
                    <a href={confirmationData.linkUrl} target="_blank" rel="noreferrer">
                      Abrir link de pagamento
                    </a>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigator.clipboard.writeText(confirmationData.linkUrl || '')}
                  >
                    Copiar link de pagamento
                  </Button>
                </div>
              ) : (
                <div className="rounded border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                  O link de pagamento foi enviado para o email cadastrado.
                </div>
              )
            )}

            {confirmationData?.contractUrl && (
              <Button asChild variant="outline" className="w-full">
                <a href={confirmationData.contractUrl} target="_blank" rel="noreferrer">
                  Ver contrato assinado
                </a>
              </Button>
            )}
          </div>

          <DialogFooter>
            <Button
              onClick={handleCloseConfirmation}
              className="bg-[#354660] text-white hover:bg-[#29374B] dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={avisoDemoOpen} onOpenChange={setAvisoDemoOpen}>
        <DialogContent className="max-w-[calc(100%-1.5rem)] sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Conta de demonstração</DialogTitle>
            <DialogDescription>
              Venda não finalizada. Em conta de demonstração, nenhuma venda é enviada à administradora.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setAvisoDemoOpen(false)}
              className="bg-[#354660] text-white hover:bg-[#29374B] dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Erro de venda em modal persistente (não some como toast). O form de
          venda continua aberto atrás pra o vendedor ajustar (ex.: ligar seguro). */}
      <Dialog open={saleError !== null} onOpenChange={(isOpen) => !isOpen && setSaleError(null)}>
        <DialogContent className="max-w-[calc(100%-1.5rem)] sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Não foi possível concluir a venda</DialogTitle>
            <DialogDescription>
              {salePodeTentarDeNovo
                ? 'A venda não foi finalizada. Os dados foram mantidos para nova tentativa.'
                : 'A venda não foi finalizada. Corrija o motivo abaixo antes de tentar novamente.'}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-foreground whitespace-pre-line">
            {saleError}
          </div>
          <DialogFooter>
            {salePodeTentarDeNovo && (
              <Button variant="outline" onClick={() => setSaleError(null)}>
                Fechar
              </Button>
            )}
            <Button
              onClick={() => {
                setSaleError(null);
                if (salePodeTentarDeNovo) void handleSave();
              }}
              className="bg-[#354660] text-white hover:bg-[#29374B] dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
            >
              {salePodeTentarDeNovo ? 'Tentar novamente' : 'Fechar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
